import { initializeApp, getApps, getApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, deleteToken, Messaging } from 'firebase/messaging';
import { dotenv } from '@/utils/constants/env';

export type ForegroundMessagePayload = {
  title?: string;
  body?: string;
  data?: Record<string, string>;
};

export type ForegroundMessageHandler = (payload: ForegroundMessagePayload) => void;

const firebaseConfig = {
  apiKey: dotenv.VITE_FIREBASE_API_KEY,
  authDomain: dotenv.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: dotenv.VITE_FIREBASE_PROJECT_ID,
  storageBucket: dotenv.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: dotenv.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: dotenv.VITE_FIREBASE_APP_ID,
};

async function waitForActivation(registration: ServiceWorkerRegistration): Promise<void> {
  if (registration.active) return;
  const worker = registration.installing || registration.waiting;
  if (!worker) return;
  await new Promise<void>((resolve) => {
    worker.addEventListener('statechange', () => {
      if (worker.state === 'activated') resolve();
    });
  });
}

class PushNotificationService {
  private static _instance: PushNotificationService;
  private _messaging: Messaging | null = null;
  private _swRegistration: ServiceWorkerRegistration | null = null;
  private _foregroundHandlers: ForegroundMessageHandler[] = [];
  private _unsubscribeForeground: (() => void) | null = null;

  static getInstance(): PushNotificationService {
    if (!PushNotificationService._instance) {
      PushNotificationService._instance = new PushNotificationService();
    }
    return PushNotificationService._instance;
  }

  private _isConfigured(): boolean {
    return !!(
      dotenv.VITE_FIREBASE_API_KEY &&
      dotenv.VITE_FIREBASE_PROJECT_ID &&
      dotenv.VITE_FIREBASE_MESSAGING_SENDER_ID &&
      dotenv.VITE_FIREBASE_VAPID_KEY
    );
  }

  /**
   * Requests permission, registers the service worker, and returns the FCM token.
   * Returns null if push is not supported, config is missing, or permission is denied.
   */
  async setup(): Promise<string | null> {
    if (!this._isConfigured()) {
      console.warn('[Push] Firebase config not set — push notifications disabled.');
      return null;
    }

    if (!('serviceWorker' in navigator) || !('Notification' in window) || !('PushManager' in window)) {
      return null;
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;

    try {
      // firebase-messaging-sw.js is generated per-environment (scripts/createEnv.cjs) with
      // the Firebase config already baked in and initialized synchronously on evaluation —
      // required so its onBackgroundMessage listener registers during the SW's initial
      // evaluation (see comment in createEnv.cjs for why runtime config passing broke this).
      this._swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      // A freshly-created registration (e.g. first visit, or after clearing site data) resolves
      // while the worker is still installing/waiting — getToken()'s pushManager.subscribe() call
      // requires an ACTIVE worker, so wait for that state before proceeding.
      await waitForActivation(this._swRegistration);

      // Init Firebase in main thread
      const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
      this._messaging = getMessaging(app);

      const token = await getToken(this._messaging, {
        vapidKey: dotenv.VITE_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: this._swRegistration,
      });

      // Wire foreground handler (onMessage only fires when app is in foreground)
      this._unsubscribeForeground?.();
      this._unsubscribeForeground = onMessage(this._messaging, (payload) => {
        const { title, body } = payload.notification || {};
        this._foregroundHandlers.forEach((h) =>
          h({ title: title ?? undefined, body: body ?? undefined, data: payload.data })
        );
      });

      return token;
    } catch (err) {
      console.error('[Push] Setup error:', err);
      return null;
    }
  }

  /**
   * Deletes the FCM token. Call on logout.
   */
  async teardown(): Promise<void> {
    this._unsubscribeForeground?.();
    this._unsubscribeForeground = null;

    try {
      if (this._messaging) {
        await deleteToken(this._messaging);
      }
    } catch {
      // ignore — token may already be invalid
    }

    this._messaging = null;
    this._swRegistration = null;
  }

  /**
   * Register a handler for push messages received while the app is in the foreground.
   * Returns an unsubscribe function.
   */
  onForegroundMessage(handler: ForegroundMessageHandler): () => void {
    this._foregroundHandlers.push(handler);
    return () => {
      this._foregroundHandlers = this._foregroundHandlers.filter((h) => h !== handler);
    };
  }
}

export const pushNotificationService = PushNotificationService.getInstance();
